// SPDX-License-Identifier: AGPL-3.0-or-later

// Body/frameset handlers for these events belong to the window, as in a normal page.
const WINDOW_EVENTS = new Set([
	'load',
	'resize',
	'scroll',
	'hashchange',
	'popstate',
	'message',
	'beforeunload',
	'unload',
	'pageshow',
	'pagehide',
	'online',
	'offline',
	'storage',
	'error',
	'focus',
	'blur',
]);

// Keeps handler code from closing or re-opening the generated <script> element.
const scriptText = (code: string) => code.replace(/<\/(script)/gi, '<\\/$1').replace(/<!--/g, '<\\x21--');

/**
 * The inherited app CSP blocks inline `on*` attributes (a nonce only covers <script> elements), so
 * they move into one generated script that attaches them with addEventListener. Like a browser,
 * the handler sees `this` and `event`, with the element, its form and the document in scope, and
 * returning false cancels the event. Handlers added later through innerHTML are not covered.
 */
function moveInlineHandlers(doc: Document): string | null {
	const handlers: Array<string> = [];
	let next = 0;
	for (const element of doc.querySelectorAll('*')) {
		const attributes = [...element.attributes].filter(
			(attribute) => /^on[a-z]+$/i.test(attribute.name) && attribute.name.toLowerCase() in element,
		);
		if (!attributes.length) continue;
		const id = String(++next);
		const onWindow = element === doc.body || element.localName === 'frameset';
		for (const {name, value} of attributes) {
			element.removeAttribute(name);
			const type = name.slice(2).toLowerCase();
			const target = onWindow && WINDOW_EVENTS.has(type) ? 1 : 0;
			handlers.push(
				`[${JSON.stringify(id)},${JSON.stringify(type)},${target},function(__konvoElement){return function(event){with(document){with(__konvoElement.form||{}){with(__konvoElement){\n${value}\n}}}};}]`,
			);
		}
		element.setAttribute('data-konvo-on', id);
	}
	if (!handlers.length) return null;
	// The handlers are created at the top level so page code cannot see this script's own variables.
	return scriptText(`(function(handlers){handlers.forEach(function(entry){
	document.querySelectorAll('[data-konvo-on="'+entry[0]+'"]').forEach(function(element){
		var handler=entry[3](element);
		(entry[2]?window:element).addEventListener(entry[1],function(event){
			if(handler.call(this,event)===false)event.preventDefault();
		});
	});
});})([
${handlers.join(',\n')}
]);`);
}

// Runs self-contained attachment scripts in an opaque-origin iframe. Remote
// resources, forms, nested frames and access to Konvo's origin remain blocked, except Google Fonts:
// pages agents write often draw icons with Material Icons/Symbols ligatures, which show as plain
// words ("thermostat") without the font. Only the stylesheet and font files, never script.
// `webSources` lets pictures, sound, video and fonts in from those places too (Konvo's media proxy,
// for an agent's page).
export function htmlPreviewDocument(source: string, webSources: Array<string> = []): string {
	// srcdoc inherits the app CSP. Give inline scripts the app's nonce so they
	// also pass that policy, without relaxing the main application's script policy.
	const nonce = document.querySelector<HTMLScriptElement>('script[nonce]')?.nonce;
	const doc = new DOMParser().parseFromString(source, 'text/html');
	const handlers = moveInlineHandlers(doc);
	if (handlers) {
		const script = doc.createElement('script');
		script.textContent = handlers;
		doc.body.append(script);
	}
	for (const script of doc.querySelectorAll('script')) {
		script.removeAttribute('nonce');
		if (nonce && !script.hasAttribute('src')) script.setAttribute('nonce', nonce);
	}
	for (const element of doc.querySelectorAll('base, meta[http-equiv]')) element.remove();
	const policy = doc.createElement('meta');
	policy.httpEquiv = 'Content-Security-Policy';
	const web = webSources.filter((source) => /^https?:\/\/[^\s;,'"]+$/i.test(source));
	const sources = (...fixed: Array<string>) => [...fixed, ...web].join(' ');
	policy.content = `default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline' https://fonts.googleapis.com; img-src ${sources('data:', 'blob:')}; font-src ${sources('data:', 'https://fonts.gstatic.com')}; media-src ${sources('data:', 'blob:')}; connect-src 'none'; frame-src 'none'; object-src 'none'; form-action 'none'; base-uri 'none'`;
	doc.head.prepend(policy);
	return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}
